import { Link } from 'react-router-dom';
import { ShoppingBag, Truck } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCartActions } from '@/context/CartContext';
import { useTheme } from '@/context/ThemeContext';
import type { Product } from '@/data/products';
import { prefetchProduct, seedProductCache } from '@/data/products.queries';
import { r2Url } from '@/lib/r2';
import { logger } from '@/lib/logger';

const PLACEHOLDER_IMG = 'https://placehold.co/700x700/png?text=KG+Goods';

type UbuyProductCardProps = {
  product: Product;
  badge?: string;
  compact?: boolean;
  fluid?: boolean;
};

export default function UbuyProductCard({ product, badge, compact = false, fluid = false }: UbuyProductCardProps) {
  const { addItem } = useCartActions();
  const { language } = useTheme();
  const queryClient = useQueryClient();
  const defaultVariant = product.variants?.[0];
  const imgSrc = r2Url(defaultVariant?.imagePath ?? product.image_path ?? '') || PLACEHOLDER_IMG;
  const displayPrice = defaultVariant?.price ?? product.price ?? 0;
  const originalPrice = defaultVariant?.originalPrice ? Number(defaultVariant.originalPrice) : null;
  const hasDiscount = originalPrice !== null && originalPrice > Number(displayPrice);
  const discountPercent = hasDiscount ? Math.round(((originalPrice - Number(displayPrice)) / originalPrice) * 100) : null;

  const handlePrefetch = () => {
    if (!product.slug) return;
    seedProductCache(queryClient, product);
    prefetchProduct(queryClient, product.slug);
  };

  const handleAddToCart = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!defaultVariant) {
      logger.error('Product has no variants:', product);
      return;
    }

    addItem(product, defaultVariant, null);
  };

  return (
    <article
      data-testid="product-card"
      className={`group flex h-full snap-start flex-col overflow-hidden rounded-[15px] border border-[var(--ubuy-border-soft)] bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-[var(--ubuy-card-shadow)] ${
        fluid
          ? 'w-full min-w-0'
          : compact
            ? 'w-[162px] min-w-[162px] sm:w-[190px] sm:min-w-[190px]'
            : 'w-[245px] min-w-[245px] sm:w-[286px] sm:min-w-[286px]'
      }`}
    >
      <Link
        to={`/product/${product.slug}`}
        state={{ product }}
        onMouseEnter={handlePrefetch}
        onFocus={handlePrefetch}
        onTouchStart={handlePrefetch}
        className="flex h-full flex-col"
      >
        <div className={`relative bg-[var(--ubuy-subtle-surface)] ${compact ? 'h-[136px]' : fluid ? 'h-[190px] sm:h-[215px]' : 'h-[210px]'}`}>
          <img
            src={imgSrc}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            onError={(event) => {
              event.currentTarget.src = PLACEHOLDER_IMG;
            }}
          />
          <div className="absolute left-2 top-2 flex flex-wrap gap-1.5">
            {badge ? (
              <span className="rounded bg-[var(--ubuy-accent)] px-2 py-1 text-[10px] font-bold uppercase text-[var(--ubuy-title)]">
                {badge}
              </span>
            ) : null}
            {discountPercent ? (
              <span className="rounded bg-[var(--ubuy-danger)] px-2 py-1 text-[10px] font-bold text-white">
                -{discountPercent}%
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-1 flex-col p-3">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-[var(--ubuy-muted)]">
            <Truck size={13} className="text-[var(--ubuy-accent)]" />
            <span>{language === 'mn' ? 'Cargo сонголттой' : 'Cargo branch ready'}</span>
          </div>

          <h3 className={`${compact ? 'min-h-[42px] text-[12px] leading-[18px]' : 'min-h-[48px] text-sm leading-6'} line-clamp-2 font-semibold text-[var(--ubuy-title)]`}>
            {product.name}
          </h3>

          {product.brandName || product.category ? (
            <p className="mt-1 line-clamp-1 text-[11px] text-[var(--ubuy-muted)]">
              {product.brandName || product.category}
            </p>
          ) : null}

          <div className="mt-auto flex items-end justify-between gap-3 pt-3">
            <div className="min-w-0">
              <p className={`${compact ? 'text-sm' : 'text-base'} font-bold text-[var(--ubuy-title)]`}>
                ₮{Number(displayPrice).toLocaleString()}
              </p>
              {hasDiscount && originalPrice ? (
                <p className="text-[11px] text-[var(--ubuy-soft-muted)] line-through">
                  ₮{originalPrice.toLocaleString()}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              data-testid="add-to-cart-btn"
              onClick={handleAddToCart}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--ubuy-title)] text-white transition hover:bg-[var(--ubuy-accent)] hover:text-[var(--ubuy-title)]"
              aria-label={language === 'mn' ? 'Сагсанд нэмэх' : 'Add to cart'}
            >
              <ShoppingBag size={17} />
            </button>
          </div>
        </div>
      </Link>
    </article>
  );
}
